/**
 * 权限认证上下文
 * 提供用户认证状态和权限管理功能
 */

import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { message } from 'antd';
import { User, AuthContextType, LoginRequest, UserRole } from '../types';
import * as api from '../services/api';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth必须在AuthProvider内部使用');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    // 从localStorage获取存储的用户信息
    const storedUser = localStorage.getItem('user');
    try {
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);

  // 计算是否已认证
  const isAuthenticated = useMemo(() => {
    return !!(user && localStorage.getItem('auth_token'));
  }, [user]);

  // 计算是否为管理员
  const isAdmin = useMemo(() => {
    return user?.role === 'admin';
  }, [user]);

  // 初始化时验证用户状态
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token && !user) {
        try {
          // 验证token并获取最新用户信息
          const currentUser = await api.getCurrentUser();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } catch (error) {
          // token无效，清除本地存储
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []); // 移除user依赖，避免无限循环

  // 监听localStorage变化，处理跨标签页同步
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' || e.key === 'auth_token') {
        // 当localStorage中的用户信息或token发生变化时，更新状态
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('auth_token');

        if (storedUser && token) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
          } catch {
            setUser(null);
          }
        } else {
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 刷新用户信息
  const refreshUser = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const currentUser = await api.getCurrentUser();
        setUser(currentUser);
        localStorage.setItem('user', JSON.stringify(currentUser));
      }
    } catch (error) {
      console.error('刷新用户信息失败:', error);
    }
  };

  // 登录函数
  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      setLoading(true);
      const response = await api.loginUser(credentials);

      // 存储用户信息和token
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('auth_token', response.token);

      message.success('登录成功');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '登录失败';
      message.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 登出函数
  const logout = (): void => {
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    message.success('已退出登录');
  };

  // 更新用户角色
  const updateUserRole = async (userId: number, role: UserRole): Promise<void> => {
    try {
      await api.updateUserRole(userId, role);

      // 如果更新的是当前用户，更新本地状态
      if (user && user.id === userId) {
        const updatedUser = { ...user, role };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      message.success('用户角色更新成功');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '角色更新失败';
      message.error(errorMessage);
      throw error;
    }
  };

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    updateUserRole,
    refreshUser
  };

  // 如果正在初始化，显示加载状态
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>正在加载...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};